import { useState } from 'react';
import { subscribeToNewsletter } from '../services/newsletter';
import { useLanguage } from '../context/LanguageContext';

export type NewsletterStatus = 'idle' | 'loading' | 'success' | 'error';

interface UseNewsletterReturn {
  status: NewsletterStatus;
  message: string;
  subscribe: (email: string) => Promise<boolean>;
}

export const useNewsletter = (): UseNewsletterReturn => {
  const { t } = useLanguage();
  const [status, setStatus] = useState<NewsletterStatus>('idle');
  const [message, setMessage] = useState('');

  const subscribe = async (email: string): Promise<boolean> => {
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage(t('newsletter', 'errorEmail'));
      return false;
    }

    setStatus('loading');
    setMessage('');

    try {
      await subscribeToNewsletter(email);
      setStatus('success');
      setMessage(t('newsletter', 'successMessage'));
      return true;
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : t('newsletter', 'errorGeneral'));
      return false;
    } finally {
      // Reset status after 5 seconds using functional update to avoid stale closure
      setTimeout(() => {
        setStatus((currentStatus) => currentStatus !== 'loading' ? 'idle' : currentStatus);
        setMessage((currentMsg) => currentMsg ? '' : currentMsg);
      }, 5000);
    }
  };

  return {
    status,
    message,
    subscribe
  };
};